import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { FivePartReasoning } from "@/components/FivePartReasoning";
import { DecisionCard } from "@/components/DecisionCard";
import { t } from "@/i18n";
import { Activity, Filter, ChevronRight, ChevronDown, Info, Loader2, X } from "lucide-react";
import { adviceTargetLabel, channelLabelForService } from "@/utils/adviceTargetLabel";

const fmtEUR = (n) => (n == null ? "—" : `€ ${Number(n).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const OUTCOME_STYLE = {
  PROPOSED:          "text-info-blue",
  VALIDATED_LATER:   "text-pos-green",
  INVALIDATED_LATER: "text-neg-red",
  INCONCLUSIVE:      "text-tier-c-grey",
  APPROVED_CONCEPTUALLY: "text-info-blue",
  REJECTED_BY_USER:  "text-text-muted",
};

const OUTCOME_FILTER_STYLE = {
  PROPOSED:          "text-info-blue border-info-blue/50",
  VALIDATED_LATER:   "text-pos-green border-pos-green/50",
  INVALIDATED_LATER: "text-neg-red border-neg-red/50",
  INCONCLUSIVE:      "text-tier-c-grey border-tier-c-grey/50",
  APPROVED_CONCEPTUALLY: "text-info-blue border-info-blue/50",
  REJECTED_BY_USER:  "text-text-muted border-border-hairline",
};

const STATUS_STYLE = {
  ACTIVE:     "bg-info-blue/10 text-info-blue border-info-blue/40",
  RECONSIDER: "bg-warn-amber/10 text-warn-amber border-warn-amber/40",
  EXPIRED:    "bg-bg-panel-raised text-text-muted border-border-hairline",
  WITHDRAWN:  "bg-bg-panel-raised text-text-muted border-border-hairline",
};

function _deriveStatusClient(g) {
  if (g.outcome === "REJECTED_BY_USER") return "WITHDRAWN";
  const now = Date.now();
  const exp = g.expires_at ? new Date(g.expires_at).getTime() : null;
  if (exp && exp < now) return "EXPIRED";
  const rev = g.review_at ? new Date(g.review_at).getTime() : null;
  if (rev && rev < now && (!g.outcome || g.outcome === "PROPOSED")) return "RECONSIDER";
  return "ACTIVE";
}

export default function GhostLedgerPage() {
  const [ledger, setLedger] = useState(null);
  const [perf, setPerf] = useState(null);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [cardMap, setCardMap] = useState({});
  const [searchParams] = useSearchParams();
  const [deeplinkError, setDeeplinkError] = useState(null);
  const [filterChannel, setFilterChannel] = useState("all");
  const [filterOutcome, setFilterOutcome] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  // Date-range filter (Fase 3 completering, Item 1). ISO dates (YYYY-MM-DD).
  const [datePreset, setDatePreset] = useState(null);  // "today"|"week"|"month"|"custom"|null
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const dateParams = useMemo(() => {
    if (!dateFrom && !dateTo) return "";
    const parts = [];
    if (dateFrom) parts.push(`from=${encodeURIComponent(dateFrom)}`);
    if (dateTo)   parts.push(`to=${encodeURIComponent(dateTo)}`);
    return parts.length ? `?${parts.join("&")}` : "";
  }, [dateFrom, dateTo]);

  const reload = useCallback(() => {
    Promise.all([
      api.get(`/opportunities/ghost-ledger${dateParams}`),
      api.get("/opportunities/ghost-performance"),
    ])
      .then(([l, p]) => { setLedger(l.data); setPerf(p.data); })
      .catch((e) => setError(e?.response?.data?.detail || t("common.error_generic")));
  }, [dateParams]);

  useEffect(() => { reload(); }, [reload]);

  // Deeplink-handler `?open=<identity_key>` — open drawer zodra lijst er is.
  // Zoekt item op `identity_key` (g4_rules) OF `id`. Als niet gevonden:
  // toont fout-state ("Advies niet gevonden of geen toegang").
  useEffect(() => {
    const openKey = searchParams.get("open");
    if (!openKey || !ledger?.items) return;
    if (openId) return;  // gebruiker heeft al iets open — respecteer keuze
    const match = ledger.items.find(
      (g) => g.identity_key === openKey || g.id === openKey);
    if (match) {
      setOpenId(match.id);
      loadCard(match.id);
      setDeeplinkError(null);
    } else {
      setDeeplinkError(`Advies niet gevonden of geen toegang (sleutel: ${openKey.slice(0, 40)}${openKey.length > 40 ? "…" : ""}).`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, ledger]);

  const loadCard = async (id) => {
    if (cardMap[id]) return;
    try {
      const { data } = await api.get(`/ghost-ledger/${id}/decision-card`);
      setCardMap((m) => ({ ...m, [id]: data }));
    } catch (e) {
      setCardMap((m) => ({ ...m, [id]: { error: e?.response?.data?.detail || "Kaart niet beschikbaar" } }));
    }
  };

  const toggleOpen = (id) => {
    const next = openId === id ? null : id;
    setOpenId(next);
    if (next) loadCard(next);
  };

  const evaluateNow = async (id) => {
    try {
      await api.post(`/ghost-ledger/${id}/evaluate`);
      setCardMap((m) => { const n = { ...m }; delete n[id]; return n; });
      await loadCard(id);
      reload();
    } catch (e) { /* silent — UI shows next reload */ }
  };

  const channels = useMemo(() => {
    const s = new Set(["all"]);
    (ledger?.items || []).forEach((g) => g.channel && s.add(g.channel));
    return Array.from(s);
  }, [ledger]);

  // M128 G4 — g4_rules-items hebben body inline; open-drawer render't
  // direct uit `item.body` in plaats van via `loadCard`-detail-fetch.
  const G4_ZEK_NL = { hoog: "Hoog", middel: "Middel", laag: "Laag" };
  const outcomes = useMemo(() => {
    const s = new Set(["all"]);
    (ledger?.items || []).forEach((g) => g.outcome && s.add(g.outcome));
    return Array.from(s);
  }, [ledger]);

  const filtered = useMemo(() => {
    return (ledger?.items || []).filter((g) => {
      if (filterChannel !== "all" && g.channel !== filterChannel) return false;
      if (filterOutcome !== "all" && g.outcome !== filterOutcome) return false;
      if (filterStatus !== "all" && _deriveStatusClient(g) !== filterStatus) return false;
      return true;
    });
  }, [ledger, filterChannel, filterOutcome, filterStatus]);

  if (!ledger || !perf) {
    return <AppLayout><div className="text-text-muted">{t("common.loading")}</div></AppLayout>;
  }

  return (
    <AppLayout>
      <div className="mb-8 flex items-center gap-3">
        <Activity className="w-6 h-6 text-accent-teal" />
        <div>
          <h1 className="text-2xl font-display font-bold text-text-primary tracking-tight" data-testid="ghost-ledger-title">
            {t("ledger.title")}
          </h1>
          <p className="text-sm text-text-muted mt-0.5 max-w-3xl">{t("ledger.subtitle")}</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-3 rounded-md border border-neg-red/40 bg-neg-red/10 text-neg-red text-sm">{error}</div>
      )}

      {/* Ghost Performance rollup */}
      <div className="mb-8 rounded-xl border border-border-hairline bg-bg-panel p-6"
           data-testid="ghost-performance-section">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-xs text-text-muted font-semibold">
            {t("ledger.perf_title")}
          </h3>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
          <PerfCounter testid="perf-analyzed" label={t("ledger.perf_analyzed")}
                       value={ledger.stats.analyzed} />
          <PerfCounter testid="perf-opps" label={t("ledger.perf_opps")}
                       value={ledger.stats.opportunities_open} />
          <PerfCounter testid="perf-decisions" label={t("ledger.perf_decisions")}
                       value={perf.total_ghost_decisions} />
          <PerfCounter testid="perf-validated" label={t("ledger.perf_validated")}
                       value={perf.outcomes?.VALIDATED_LATER || 0} />
          <PerfCounter testid="perf-invalidated" label={t("ledger.perf_invalidated")}
                       value={perf.outcomes?.INVALIDATED_LATER || 0} />
          <PerfCounter testid="perf-inconclusive" label={t("ledger.perf_inconclusive")}
                       value={(perf.outcomes?.PROPOSED || 0) + (perf.outcomes?.INCONCLUSIVE || 0)} />
        </div>

        {(() => {
          const r = perf.hypothetical_impact_range;
          const lo = r?.low_eur;
          const hi = r?.high_eur;
          const total = perf.total_ghost_decisions || 0;
          const isEmpty = !r || (!lo && !hi) || total === 0;
          if (isEmpty) {
            // M128 G4 — als er wél adviezen zijn maar geen expliciete
            // waarschijnlijke_impact-som, toon "Waarschijnlijke opbrengst:
            // onbekend" i.p.v. "Nog geen adviezen om een schatting op te
            // baseren".
            const advicesCount = (ledger?.items || []).length;
            return (
              <div className="p-3 rounded-md border border-border-hairline bg-bg-panel-raised" data-testid="perf-hypothetical-empty">
                <div className="text-[10px] text-text-muted font-semibold mb-1">
                  {t("ledger.perf_hypothetical_range")}
                </div>
                <div className="text-sm text-text-primary font-mono tabular-nums" data-testid="perf-hypothetical-range">
                  {advicesCount > 0 ? "Waarschijnlijke opbrengst: onbekend" : "Nog niet te bepalen"}
                </div>
                <div className="text-xs text-text-muted mt-1.5 leading-relaxed">
                  {advicesCount > 0
                    ? "Er zijn adviezen, maar zonder onderbouwde impact-schatting."
                    : "Nog geen adviezen om een schatting op te baseren."}
                </div>
              </div>
            );
          }
          return (
            <div className="p-3 rounded-md border border-warn-amber/40 bg-warn-amber/10">
              <div className="text-[10px] text-warn-amber font-semibold mb-1 flex items-center gap-2">
                {t("ledger.perf_hypothetical_range")}
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-warn-amber/20 text-warn-amber border border-warn-amber/50">
                  {t("ledger.hypothesis_label")}
                </span>
              </div>
              <div className="text-sm text-warn-amber font-mono tabular-nums" data-testid="perf-hypothetical-range">
                {fmtEUR(lo)} – {fmtEUR(hi)}
              </div>
              <div className="text-xs text-warn-amber mt-1.5 leading-relaxed" data-testid="perf-disclaimer">
                {t("ledger.perf_disclaimer")}
              </div>
            </div>
          );
        })()}
        <div className="mt-3 flex items-start gap-2 text-[11px] text-text-muted">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{perf.note_nl}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <DateRangeFilter
          preset={datePreset} from={dateFrom} to={dateTo}
          onPreset={(preset, from, to) => {
            setDatePreset(preset); setDateFrom(from); setDateTo(to);
          }}
        />
        <div className="flex items-center gap-1 text-xs text-text-muted">
          <Filter className="w-3.5 h-3.5" />
          {t("ledger.filter_channel")}:
        </div>
        <div className="flex gap-1" role="radiogroup">
          {channels.map((c) => (
            <button key={c}
                    onClick={() => setFilterChannel(c)}
                    data-testid={`ledger-filter-channel-${c}`}
                    className={`px-2.5 h-7 rounded text-[11px] ${ filterChannel === c ? "bg-info-blue/20 text-info-blue border border-info-blue/50" : "bg-bg-panel text-text-muted border border-border-hairline hover:border-accent-teal/40" }`}>
              {c === "all" ? t("ledger.filter_all") : c}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 text-xs text-text-muted ml-4">
          {t("ledger.filter_outcome")}:
        </div>
        <div className="flex gap-1" role="radiogroup">
          {outcomes.map((o) => {
            const isActive = filterOutcome === o;
            const tint = o !== "all" ? OUTCOME_FILTER_STYLE[o] : null;
            return (
              <button key={o}
                    onClick={() => setFilterOutcome(o)}
                    data-testid={`ledger-filter-outcome-${o}`}
                    className={`px-2.5 h-7 rounded text-xs text-text-secondary border transition-colors duration-exec ${
                      isActive
                        ? (tint ? `${tint} bg-bg-panel-raised` : "bg-info-blue/20 text-info-blue border-info-blue/50")
                        : (tint ? `${tint.split(" ")[0]}/80 border-border-hairline hover:border-accent-teal/40 bg-bg-panel` : "bg-bg-panel text-text-muted border-border-hairline hover:border-accent-teal/40")
                    }`}>
                {o === "all" ? t("ledger.filter_all") : t(`enum.outcome.${o}`)}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1 text-xs text-text-muted ml-4">
          {t("ledger.filter_status") || "Status"}:
        </div>
        <div className="flex gap-1" role="radiogroup">
          {["all", "ACTIVE", "RECONSIDER", "EXPIRED", "WITHDRAWN"].map((o) => (
            <button key={o}
                    onClick={() => setFilterStatus(o)}
                    data-testid={`ledger-filter-status-${o}`}
                    className={`px-2.5 h-7 rounded text-[11px] ${ filterStatus === o ? "bg-info-blue/20 text-info-blue border border-info-blue/50" : "bg-bg-panel text-text-muted border border-border-hairline hover:border-accent-teal/40" }`}>
              {o === "all" ? t("ledger.filter_all") : t(`enum.status.${o}`)}
            </button>
          ))}
        </div>
      </div>
      {filtered.length === 0 ? (
        <div data-testid="ledger-empty" className="p-8 rounded-xl border border-border-hairline bg-bg-panel text-center text-sm text-text-muted">
          {t("ledger.empty")}
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((g) => (
            <li key={g.id} data-testid={`ledger-item-${g.id}`}
                className="rounded-xl border border-border-hairline bg-bg-panel">
              <button
                onClick={() => toggleOpen(g.id)}
                data-testid={`ledger-item-toggle-${g.id}`}
                className="w-full text-left p-4 flex items-start gap-3 hover:bg-bg-panel"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg-panel-raised text-text-primary/85 border border-border-hairline">
                      {g.channel || "—"}
                    </span>
                    <span className={`pill-outline ${OUTCOME_STYLE[g.outcome] || OUTCOME_STYLE.PROPOSED}`}>
                      {t(`enum.outcome.${g.outcome || "PROPOSED"}`)}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold ${STATUS_STYLE[_deriveStatusClient(g)]}`}
                          data-testid={`ledger-derived-status-${g.id}`}>
                      {t(`enum.status.${_deriveStatusClient(g)}`)}
                    </span>
                    {/* M128 G4 — verberg `advice_rule_id` (bv. ADS.PACING.OVERSHOOT)
                        op de rij zelf. Het is technische metadata die alleen
                        onder "Bewijs" in de drawer thuishoort. */}
                    {g.source_kind !== "g4_rules" && (
                      <span className="text-[10px] text-text-muted">
                        {g.action_type}
                      </span>
                    )}
                    {g.source_kind === "g4_rules" && g.service && (
                      <span className="text-[9px] text-accent-teal font-bold border border-info-blue/40 bg-info-blue/10 px-1.5 py-0.5 rounded"
                            data-testid={`ledger-channel-badge-${g.id}`}>
                        {channelLabelForService(g.service)}
                      </span>
                    )}
                    {g.evidence_class && g.source_kind !== "g4_rules" && (
                      <span className="text-[10px] text-text-muted">
                        · {g.evidence_class}
                      </span>
                    )}
                    <span className="text-[10px] text-text-subtle ml-auto">
                      {new Date(g.created_at).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" })}
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-text-primary mb-1">{g.title}</div>
                  {g.source_kind === "g4_rules" && (() => {
                    const tgt = adviceTargetLabel(g.service, g.advice_rule_id, g.target_entity_id);
                    return tgt ? (
                      <div className="text-[11px] text-text-muted mb-1"
                           data-testid={`ledger-target-label-${g.id}`}>
                        <span className="text-text-primary/85">{tgt.label}</span>
                        {tgt.path && (
                          <span className="ml-1 text-text-subtle">({tgt.path})</span>
                        )}
                      </div>
                    ) : null;
                  })()}
                  {g.reasoning_summary?.expected_impact_nl && (
                    <div className="text-xs text-text-muted leading-relaxed flex items-center gap-2">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-warn-amber/10 text-warn-amber border border-warn-amber/40">
                        {t("ledger.hypothesis_label")}
                      </span>
                      {g.reasoning_summary.expected_impact_nl}
                    </div>
                  )}
                </div>
                <div className="pt-1 text-text-subtle shrink-0">
                  {openId === g.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </div>
              </button>

              {openId === g.id && (
                <div className="border-t border-border-hairline p-5 bg-bg-panel"
                     data-testid={`ledger-item-detail-${g.id}`}>
                  {g.source_kind === "g4_rules" ? (
                    <div className="space-y-3" data-testid={`ledger-g4-detail-${g.id}`}>
                      <G4Section label="Wat ik zie" value={g.body?.wat_ik_zie} />
                      <G4Section label="Waarom dit belangrijk is" value={g.body?.waarom_belangrijk} />
                      <G4Section label="Wat ik adviseer" value={g.body?.wat_ik_adviseer} />
                      <G4Section label="Waarschijnlijke kosten of opbrengst"
                                 value={g.body?.waarschijnlijke_impact || "onbekend"} />
                      <div>
                        <div className="text-[10px] text-text-muted font-semibold mb-1">
                          Hoe zeker ik ben
                        </div>
                        <div className="text-xs text-text-primary" data-testid={`ledger-g4-zekerheid-${g.id}`}>
                          {G4_ZEK_NL[g.body?.zekerheid || "middel"] || "Middel"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-text-muted font-semibold mb-1">
                          Bewijs
                        </div>
                        <div className="text-xs text-text-muted leading-relaxed"
                             data-testid={`ledger-g4-evidence-${g.id}`}>
                          {(() => {
                            const ev = g.evidence_snapshot || {};
                            const bits = [];
                            if (ev.ads_run_id) bits.push(`Ads-rapport ${String(ev.ads_run_id).slice(0, 8)}`);
                            if (ev.gsc_run_id) bits.push(`GSC-rapport ${String(ev.gsc_run_id).slice(0, 8)}`);
                            if (ev.ga4_run_id) bits.push(`GA4-rapport ${String(ev.ga4_run_id).slice(0, 8)}`);
                            if (ev.period && ev.period.length === 2)
                              bits.push(`periode ${ev.period[0]}..${ev.period[1]}`);
                            if (ev.days_with_data != null)
                              bits.push(`${ev.days_with_data} dagen met gegevens`);
                            return bits.length ? bits.join(" · ") : "Bron: interne rapportsnapshot.";
                          })()}
                        </div>
                        {g.advice_rule_id && (
                          <div className="mt-2 text-[9px] text-text-subtle"
                                data-testid={`ledger-g4-rule-id-${g.id}`}>
                            referentie: {g.advice_rule_id}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : !cardMap[g.id] ? (
                    <div className="text-xs text-text-muted flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> {t("common.loading")}
                    </div>
                  ) : cardMap[g.id].error ? (
                    <div className="text-xs text-neg-red">{cardMap[g.id].error}</div>
                  ) : (
                    <>
                      <DecisionCard card={cardMap[g.id]} onReloadParent={() => { setCardMap((m) => { const n = { ...m }; delete n[g.id]; return n; }); loadCard(g.id); reload(); }} />
                      <div className="mt-3 flex items-center justify-end gap-2">
                        <button onClick={() => evaluateNow(g.id)}
                                 data-testid={`ledger-evaluate-${g.id}`}
                                 className="h-8 px-3 rounded-btn border border-border-hairline text-xs font-semibold text-text-primary/85 hover:bg-bg-panel">
                          {t("ledger.evaluate_now") || "Evalueer nu"}
                        </button>
                      </div>
                      <div className="mt-6 pt-4 border-t border-border-hairline">
                        <div className="text-[10px] text-text-muted font-semibold mb-2">
                          5-STAPS REDENERING
                        </div>
                        <FivePartReasoning
                          reasoning={g.reasoning_summary_five_step || {}}
                          confidence={(g.opportunity && g.opportunity.confidence)
                                       ? String(g.opportunity.confidence >= 0.7 ? t("enum.confidence_pill.HIGH")
                                                                                   : g.opportunity.confidence >= 0.5 ? t("enum.confidence_pill.MEDIUM")
                                                                                                                       : t("enum.confidence_pill.LOW"))
                                       : t("enum.confidence_pill.LOW")}
                          scoreBreakdown={g.opportunity?.opportunity_score_breakdown}
                          testidPrefix={`ledger-reasoning-${g.id}`}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </AppLayout>
  );
}

function PerfCounter({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-md border border-border-hairline bg-bg-panel p-3">
      <div className="text-[9px] text-text-muted font-semibold mb-1">
        {label}
      </div>
      <div className="text-xl font-bold text-text-primary font-mono tabular-nums">{value}</div>
    </div>
  );
}

// M128 G4 — sectie-block voor de g4-detail-drawer op /ghost-ledger.
function G4Section({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] text-text-muted font-semibold mb-1">
        {label}
      </div>
      <div className="text-xs text-text-primary leading-relaxed">{value}</div>
    </div>
  );
}

// --- Date-range filter (Fase 3 completering Item 1) --------------------
// Presets: vandaag / deze week (ISO ma) / deze maand + aangepast (van/tot).
// Sends ISO YYYY-MM-DD to parent. UI-only; parent injects into query string.
function _iso(d) {
  const yr = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${yr}-${m}-${dy}`;
}
function _rangeToday()  { const n = new Date(); return [_iso(n), _iso(n)]; }
function _rangeWeek()   {
  const n = new Date();
  const dow = (n.getDay() + 6) % 7; // ma=0
  const start = new Date(n); start.setDate(n.getDate() - dow);
  return [_iso(start), _iso(n)];
}
function _rangeMonth()  {
  const n = new Date();
  const start = new Date(n.getFullYear(), n.getMonth(), 1);
  return [_iso(start), _iso(n)];
}

function DateRangeFilter({ preset, from, to, onPreset }) {
  const presets = [
    { key: "today", label: t("ledger.date_preset_today"),  range: _rangeToday },
    { key: "week",  label: t("ledger.date_preset_week"),   range: _rangeWeek  },
    { key: "month", label: t("ledger.date_preset_month"),  range: _rangeMonth },
  ];
  const activeRangeLabel = (from || to)
    ? `${from || "…"} → ${to || "…"}`
    : null;

  return (
    <div className="flex items-center gap-2 flex-wrap"
         data-testid="ledger-date-filter">
      <div className="flex items-center gap-1 text-xs text-text-muted">
        <Filter className="w-3.5 h-3.5" />
        {t("ledger.date_range_label")}:
      </div>
      <div className="flex gap-1" role="radiogroup" aria-label={t("ledger.date_range_label")}>
        {presets.map((p) => {
          const active = preset === p.key;
          return (
            <button key={p.key}
                    type="button"
                    onClick={() => { const [f, tt] = p.range(); onPreset(p.key, f, tt); }}
                    data-testid={`ledger-date-preset-${p.key}`}
                    aria-pressed={active}
                    className={`px-2.5 h-7 rounded text-[11px] border transition-colors duration-exec ${ active ? "bg-info-blue/20 text-info-blue border-info-blue/50" : "bg-bg-panel text-text-muted border-border-hairline hover:border-accent-teal/40" }`}>
              {p.label}
            </button>
          );
        })}
        <button type="button"
                onClick={() => onPreset("custom", from || "", to || "")}
                data-testid="ledger-date-preset-custom"
                aria-pressed={preset === "custom"}
                className={`px-2.5 h-7 rounded text-[11px] border transition-colors duration-exec ${ preset === "custom" ? "bg-info-blue/20 text-info-blue border-info-blue/50" : "bg-bg-panel text-text-muted border-border-hairline hover:border-accent-teal/40" }`}>
          {t("ledger.date_preset_custom")}
        </button>
      </div>

      {preset === "custom" && (
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-text-muted">
            {t("ledger.date_from")}
            <input type="date"
                   value={from || ""}
                   onChange={(e) => onPreset("custom", e.target.value, to || "")}
                   data-testid="ledger-date-from"
                   className="ml-1 h-7 rounded bg-bg-panel border border-border-hairline px-2 text-[11px] text-text-primary focus:border-accent-teal/60 focus:outline-none" />
          </label>
          <label className="text-[10px] text-text-muted">
            {t("ledger.date_to")}
            <input type="date"
                   value={to || ""}
                   onChange={(e) => onPreset("custom", from || "", e.target.value)}
                   data-testid="ledger-date-to"
                   className="ml-1 h-7 rounded bg-bg-panel border border-border-hairline px-2 text-[11px] text-text-primary focus:border-accent-teal/60 focus:outline-none" />
          </label>
        </div>
      )}

      {activeRangeLabel && (
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-text-muted num"
                data-testid="ledger-date-active-range">
            {activeRangeLabel}
          </span>
          <button type="button"
                  onClick={() => onPreset(null, "", "")}
                  data-testid="ledger-date-reset"
                  aria-label={t("ledger.date_reset")}
                  className="h-6 w-6 inline-flex items-center justify-center rounded border border-border-hairline text-text-muted hover:text-text-primary hover:border-accent-teal/40 transition-colors duration-exec">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
